import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, RefreshCw } from 'lucide-react';
import { can, type MyClubTeamInvite, type TeamDetail, type TeamLadder, type TeamSummary } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { timeAgo } from '../../lib/format';
import { Alert, Button, Dialog, LoadingBlock, Section } from '../ui';

/** On the Teams page: a club asking to add one of your teams. */
export function ClubTeamInvites({ teams }: { teams: TeamSummary[] }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['my-club-team-invites'], queryFn: () => api.get<MyClubTeamInvite[]>('/me/club-team-invites') });
  const mine = teams.filter((t) => t.role === 'head_coach' && !t.clubName);
  const [teamId, setTeamId] = useState<Record<string, string>>({});
  const answer = useMutation({
    mutationFn: (a: { id: string; yes: boolean }) => (a.yes ? api.post(`/club-team-invites/${a.id}/accept`, { teamId: teamId[a.id] ?? mine[0]?.id }) : api.post(`/club-team-invites/${a.id}/decline`)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-club-team-invites'] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
  if (!list.data?.length) return null;
  return (
    <div className="mb-6 space-y-2">
      {list.data.map((i) => (
        <div key={i.id} className="rounded-lg border border-action/30 bg-action-soft p-4">
          <p className="flex items-start gap-2 font-medium">
            <Building2 className="mt-0.5 size-4 shrink-0 text-action" aria-hidden />
            {i.clubName} would like one of your teams to join the club{i.seasonName ? ` for ${i.seasonName}` : ''}.
          </p>
          <p className="mt-1 text-[14px] text-muted">
            The club can then help with your coaches, players and PlayHQ fixture. It can’t see your sessions or plans, and you can take the team out at any time.
          </p>
          {mine.length ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {mine.length > 1 && (
                <select className="field-input w-auto py-1.5 text-[14px]" value={teamId[i.id] ?? mine[0]!.id} onChange={(e) => setTeamId({ ...teamId, [i.id]: e.target.value })} aria-label="Which team">
                  {mine.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
              <Button loading={answer.isPending && answer.variables?.yes} onClick={() => answer.mutate({ id: i.id, yes: true })}>
                Add {mine.length === 1 ? mine[0]!.name : 'this team'}
              </Button>
              <Button variant="secondary" loading={answer.isPending && !answer.variables?.yes} onClick={() => answer.mutate({ id: i.id, yes: false })}>
                No thanks
              </Button>
            </div>
          ) : (
            <p className="mt-2 text-[14px]">You’re not head coach of a team that could join. Create your team first, then come back here.</p>
          )}
          {answer.isError && (
            <div className="mt-2">
              <Alert>{errorMessage(answer.error)}</Alert>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** The grade ladder from PlayHQ, with Sync now for coaches. */
export function LadderTab({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const ladder = useQuery({ queryKey: ['ladder', team.id], queryFn: () => api.get<TeamLadder | null>(`/teams/${team.id}/ladder`) });
  const sync = useMutation({
    mutationFn: () => api.post<{ added: number; updated: number; removed: number }>(`/teams/${team.id}/playhq/sync`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['ladder', team.id] });
      void qc.invalidateQueries({ queryKey: ['team', team.id] });
      void qc.invalidateQueries({ queryKey: ['schedule-games'] });
      void qc.invalidateQueries({ queryKey: ['season'] });
    },
  });
  const l = ladder.data;
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-muted">
          {l?.gradeName ? `${l.gradeName}, from PlayHQ` : 'From PlayHQ'}
          {l?.syncedAt ? `. Updated ${timeAgo(l.syncedAt)}.` : '.'} Games and results come in automatically every few hours.
        </p>
        {can(team.role, 'sessions:write') && (
          <Button variant="secondary" loading={sync.isPending} onClick={() => sync.mutate()}>
            <RefreshCw className="size-4" aria-hidden /> Sync now
          </Button>
        )}
      </div>
      {sync.data && <div className="mb-3"><Alert tone="ok">Up to date: {sync.data.added} new games, {sync.data.updated} updated, {sync.data.removed} removed.</Alert></div>}
      {sync.isError && <div className="mb-3"><Alert>{errorMessage(sync.error)}</Alert></div>}
      {team.playhq?.error && !sync.isError && <div className="mb-3"><Alert tone="warn">Last sync didn’t work: {team.playhq.error}</Alert></div>}
      {ladder.isLoading ? (
        <LoadingBlock />
      ) : !l?.rows.length ? (
        <p className="text-muted">No ladder yet. It shows once the grade has results.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="tnum w-full text-left text-[14px] [&_td]:px-2 [&_th]:px-2 sm:[&_td]:px-3 sm:[&_th]:px-3">
            <thead className="border-b border-line text-[13px] text-muted">
              <tr>
                <th className="w-8 px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Team</th>
                <th className="w-9 px-3 py-2 text-right font-medium" title="Played">P</th>
                <th className="w-9 px-3 py-2 text-right font-medium" title="Won">W</th>
                <th className="w-9 px-3 py-2 text-right font-medium" title="Lost">L</th>
                <th className="w-12 px-3 py-2 text-right font-medium" title="Points">Pts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {l.rows.map((r) => (
                <tr key={`${r.position}-${r.team}`} className={r.isUs ? 'bg-action-soft font-semibold' : ''}>
                  <td className="px-3 py-2">{r.position}</td>
                  <td className="max-w-0 truncate px-3 py-2" title={r.team}>{r.team}</td>
                  <td className="px-3 py-2 text-right">{r.played ?? '–'}</td>
                  <td className="px-3 py-2 text-right">{r.won ?? '–'}</td>
                  <td className="px-3 py-2 text-right">{r.lost ?? '–'}</td>
                  <td className="px-3 py-2 text-right">{r.points ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Team settings: which club it's in, and taking it out (head coach). */
export function TeamClubSection({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const leave = useMutation({
    mutationFn: () => api.del(`/teams/${team.id}/club`),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['team', team.id] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
  if (!team.club) return null;
  return (
    <Section title="Club" description={`${team.name} is part of ${team.club.name}. The club helps with coaches, players and the PlayHQ fixture; it can’t see sessions or plans.`}>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Take the team out of {team.club.name}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={`Leave ${team.club.name}?`}>
        <div className="space-y-4">
          <p className="text-[14px] text-muted">The team, its people and its sessions stay as they are. The PlayHQ link goes, and its games become ordinary games you can edit.</p>
          {leave.isError && <Alert>{errorMessage(leave.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" className="btn-danger" loading={leave.isPending} onClick={() => leave.mutate()}>
              Leave the club
            </Button>
          </div>
        </div>
      </Dialog>
    </Section>
  );
}
